import { Icon, IconSet } from '@postscript/components';
import { useAddResponseFeedback } from 'components/conversations/hooks/useResponses';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Message, Subscriber } from '../types';

const StyledThumb = styled(Icon)<{
  direction: 'up' | 'down';
  disabled?: boolean;
  feedback?: string;
}>`
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  pointer-events: ${(props) => (props.disabled ? 'none' : 'auto')};
  color: ${(props) =>
    props.feedback === ''
      ? 'var(--text-color-dim)'
      : props.feedback === 'POSITIVE' && props.direction === 'up'
      ? 'var(--green-6)'
      : props.feedback === 'NEGATIVE' && props.direction === 'down'
      ? 'var(--red-6)'
      : 'var(--text-color-dim)'};
  margin-right: ${(props) =>
    props.direction === 'up' ? 'var(--spacing-1)' : '0'};

  &:hover {
    color: ${(props) =>
      props.disabled
        ? 'var(--text-color-dim)'
        : props.direction === 'up'
        ? 'var(--green-6)'
        : 'var(--red-6)'};
    cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
    fill: ${(props) =>
      props.direction === 'up' ? 'var(--green-6)' : 'var(--red-6)'};
  }
`;

interface MessageFeedbackProps {
  message: Message;
  subscriber: Subscriber | null;
  responseType?: 'shopper' | 'sender';
}

export const MessageFeedback = ({
  message,
  subscriber,
  responseType = 'shopper',
}: MessageFeedbackProps) => {
  const [feedback, setFeedback] = useState('');
  const [allowClick, setAllowClick] = useState<boolean>(true);
  const { mutate: addResponseFeedback } = useAddResponseFeedback();
  const feedbackValue = subscriber?.chat_history?.find(
    (c) => c.sent_messages_id === message.sent_message_id,
  );

  const onClickHandler = (
    // eslint-disable-next-line camelcase
    feedback_type: 'POSITIVE' | 'NEGATIVE',
    // eslint-disable-next-line camelcase
    subscriber_id: number,
  ) => {
    return () => {
      addResponseFeedback({
        messageId: message.sent_message_id,
        feedback_type: feedback_type.toLowerCase(),
        message: message.text,
        subscriberId: subscriber_id,
        responseType,
        messageGuid: responseType === 'sender' ? message.subtype : undefined,
      });
      setFeedback(feedback_type);
      setAllowClick(false);
    };
  };

  // Add debounce to prevent user from clicking too fast
  useEffect(() => {
    const timeout = setTimeout(() => {
      setAllowClick(true);
    }, 2000);
    return () => clearTimeout(timeout);
  }, [feedback, allowClick]);

  // Use useEffect to log and set initial feedback
  useEffect(() => {
    if (message?.feedback) {
      setFeedback(message?.feedback);
    } else if (feedbackValue?.feedback) {
      setFeedback(feedbackValue.feedback);
    }
  }, [feedbackValue, message.feedback]);

  return (
    <span>
      <StyledThumb
        direction="up"
        component={
          feedback === 'POSITIVE' ? IconSet.ThumbsUpFilled : IconSet.ThumbsUp
        }
        feedback={feedback}
        size="small"
        onClick={onClickHandler('POSITIVE', subscriber?.subscriber.id ?? 0)}
        disabled={!allowClick}
        data-testid="thumbs-up"
      />
      <StyledThumb
        direction="down"
        component={
          feedback === 'NEGATIVE'
            ? IconSet.ThumbsDownFilled
            : IconSet.ThumbsDown
        }
        feedback={feedback}
        size="small"
        onClick={onClickHandler('NEGATIVE', subscriber?.subscriber.id ?? 0)}
        disabled={!allowClick}
        data-testid="thumbs-down"
      />
    </span>
  );
};
